import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import {
  FileText, Upload, Sparkles, RefreshCw, ChevronDown, Plus, Loader2,
  Layers, Target, CheckCircle, FolderKanban, GitCompare, Info,
  AlertTriangle, ArrowRight, BookTemplate, BarChart2, Filter, MessageSquare,
  Rocket, Package, ClipboardCheck, Zap, Brain, PieChart, Undo2
} from 'lucide-react';
import { api } from '../api/client';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import RequirementsHierarchy from '../components/RequirementsHierarchy';
import RequirementEditorModal from '../components/RequirementEditor';
import WBSTree from '../components/WBSTree';
import AIRequirementAssistant from '../components/AIRequirementAssistant';
import ExportDocumentsDropdown from '../components/ExportDocumentsDropdown';
import SpecUploadModal from '../components/SpecUploadModal';
import ScopingCoPilot from '../components/ScopingCoPilot';
import ScopeSummary from '../components/ScopeSummary';
import { WBSPreviewModal, RevertBaselineModal } from '../components/WBSPreviewModal';
import { PageErrorBoundary, WidgetErrorBoundary } from '../components/ErrorBoundary';
import { SkeletonCard, SkeletonKPIGrid } from '../components/Skeleton';
import EmptyState from '../components/EmptyState';
import CreateProjectModal from '../components/CreateProjectModal';

// ==========================================
// STEP PROGRESS BAR COMPONENT
// ==========================================

const WORKFLOW_STEPS = [
  { id: 'requirements', label: 'Requirements', icon: Layers },
  { id: 'wbs', label: 'WBS', icon: FolderKanban },
  { id: 'baseline', label: 'Baseline', icon: ClipboardCheck },
  { id: 'project', label: 'Project', icon: Package },
  { id: 'delivery', label: 'Delivery', icon: Rocket },
];

const StepProgressBar = ({ currentStep = 'requirements', hasRequirements, hasWBS, hasBaseline, hasProject }) => {
  const getStepStatus = (stepId) => {
    const steps = ['requirements', 'wbs', 'baseline', 'project', 'delivery'];
    const currentIndex = steps.indexOf(currentStep);
    const stepIndex = steps.indexOf(stepId);
    
    // Determine completion based on actual data
    const completion = {
      requirements: hasRequirements,
      wbs: hasWBS,
      baseline: hasBaseline,
      project: hasProject,
      delivery: false, // Future state
    };
    
    if (completion[stepId]) return 'completed';
    if (stepIndex === currentIndex) return 'current';
    if (stepIndex < currentIndex) return 'completed';
    return 'upcoming';
  };

  return (
    <div className="bg-white rounded-lg border p-4 mb-6" data-testid="workflow-progress-bar">
      <div className="flex items-center justify-between">
        {WORKFLOW_STEPS.map((step, index) => {
          const status = getStepStatus(step.id);
          const Icon = step.icon;
          
          return (
            <React.Fragment key={step.id}>
              {/* Step */}
              <div className="flex flex-col items-center">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${
                  status === 'completed' 
                    ? 'bg-green-100 text-green-600' 
                    : status === 'current'
                      ? 'bg-purple-600 text-white ring-4 ring-purple-100'
                      : 'bg-gray-100 text-gray-400'
                }`}>
                  {status === 'completed' ? (
                    <CheckCircle className="w-5 h-5" />
                  ) : (
                    <Icon className="w-5 h-5" />
                  )}
                </div>
                <span className={`mt-2 text-xs font-medium ${
                  status === 'current' ? 'text-purple-700' : 
                  status === 'completed' ? 'text-green-700' : 'text-gray-400'
                }`}>
                  {step.label}
                </span>
              </div>
              
              {/* Connector */}
              {index < WORKFLOW_STEPS.length - 1 && (
                <div className={`flex-1 h-0.5 mx-2 ${
                  getStepStatus(WORKFLOW_STEPS[index + 1].id) !== 'upcoming' 
                    ? 'bg-green-300' 
                    : 'bg-gray-200'
                }`} />
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};

// ==========================================
// DECISION PANEL COMPONENT
// ==========================================

const StartDecisionPanel = ({ onUploadSpec, onStartWizard, onOpenCoPilot, isReadOnly }) => {
  return (
    <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-xl border border-purple-100 p-6 mb-6" data-testid="start-decision-panel">
      <h3 className="text-lg font-semibold text-gray-900 mb-2">How would you like to start?</h3>
      <p className="text-sm text-gray-600 mb-6">
        ScopeOps helps you capture requirements, refine scope, generate WBS, and create baselines. 
        Choose a starting method below to begin.
      </p>
      
      <div className="grid grid-cols-3 gap-4">
        {/* Upload Spec - Fastest */}
        <div 
          className={`bg-white rounded-xl border-2 border-transparent hover:border-purple-300 p-5 transition-all cursor-pointer group ${isReadOnly ? 'opacity-50 cursor-not-allowed' : ''}`}
          onClick={() => !isReadOnly && onUploadSpec()}
          data-testid="decision-upload-spec"
        >
          <div className="flex items-center justify-between mb-3">
            <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center group-hover:bg-blue-200 transition-colors">
              <Upload className="w-6 h-6 text-blue-600" />
            </div>
            <span className="text-xs font-medium text-blue-600 bg-blue-50 px-2 py-1 rounded-full">
              Fastest
            </span>
          </div>
          <h4 className="font-semibold text-gray-900 mb-1">Upload Spec</h4>
          <p className="text-sm text-gray-500">
            Let AI extract Themes, Epics, and Requirements from a document.
          </p>
        </div>

        {/* Start Wizard - Recommended */}
        <div 
          className="bg-white rounded-xl border-2 border-purple-300 ring-2 ring-purple-100 p-5 transition-all cursor-pointer group hover:border-purple-400"
          onClick={onStartWizard}
          data-testid="decision-start-wizard"
        >
          <div className="flex items-center justify-between mb-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center">
              <Sparkles className="w-6 h-6 text-white" />
            </div>
            <span className="text-xs font-semibold text-white bg-gradient-to-r from-purple-600 to-indigo-600 px-2.5 py-1 rounded-full">
              Recommended
            </span>
          </div>
          <h4 className="font-semibold text-gray-900 mb-1">Start Wizard</h4>
          <p className="text-sm text-gray-500">
            Use the 7-step guided workflow to define and baseline your scope.
          </p>
        </div>

        {/* Use CoPilot - Manual + AI */}
        <div 
          className="bg-white rounded-xl border-2 border-transparent hover:border-purple-300 p-5 transition-all cursor-pointer group"
          onClick={onOpenCoPilot}
          data-testid="decision-open-copilot"
        >
          <div className="flex items-center justify-between mb-3">
            <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center group-hover:bg-green-200 transition-colors">
              <MessageSquare className="w-6 h-6 text-green-600" />
            </div>
            <span className="text-xs font-medium text-green-600 bg-green-50 px-2 py-1 rounded-full">
              Manual + AI
            </span>
          </div>
          <h4 className="font-semibold text-gray-900 mb-1">Use CoPilot</h4>
          <p className="text-sm text-gray-500">
            Build Themes, Epics, and Requirements manually with AI support.
          </p>
        </div>
      </div>
    </div>
  );
};

// ==========================================
// QUALITY SUMMARY CARD
// ==========================================

const QualitySummaryCard = ({ projectId, tenantId }) => {
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    if (projectId && tenantId) {
      loadSummary();
    }
  }, [projectId, tenantId]);

  const loadSummary = async () => {
    try {
      const res = await api.getProjectQualitySummary(projectId, tenantId);
      setSummary(res.data);
    } catch (err) {
      console.error('Failed to load quality summary:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <SkeletonCard />;
  }

  if (!summary || summary.total_requirements === 0) {
    return null;
  }

  const scoreColor = summary.average_score >= 80 ? 'text-green-600' :
    summary.average_score >= 60 ? 'text-amber-600' : 'text-red-600';

  return (
    <WidgetErrorBoundary widgetName="Quality Summary">
      <div className="bg-white rounded-lg border p-4">
        <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
          <BarChart2 className="w-4 h-4" />
          Requirements Quality
        </h4>
        
        <div className="grid grid-cols-3 gap-4 mb-4">
          <div className="text-center">
            <div className={`text-2xl font-bold ${scoreColor}`}>{summary.average_score}%</div>
            <div className="text-xs text-gray-500">Avg Score</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-green-600">{summary.ready_count}</div>
            <div className="text-xs text-gray-500">Ready</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-amber-600">{summary.not_ready_count}</div>
            <div className="text-xs text-gray-500">Need Work</div>
          </div>
        </div>

        {/* Issues breakdown */}
        {summary.issues && Object.values(summary.issues).some(v => v > 0) && (
          <div className="pt-3 border-t border-gray-100">
            <p className="text-xs font-medium text-gray-500 mb-2">Issues Found</p>
            <div className="space-y-1">
              {summary.issues.missing_ac > 0 && (
                <div className="flex items-center justify-between text-xs">
                  <span className="text-gray-600">Missing Acceptance Criteria</span>
                  <span className="text-amber-600 font-medium">{summary.issues.missing_ac}</span>
                </div>
              )}
              {summary.issues.missing_category > 0 && (
                <div className="flex items-center justify-between text-xs">
                  <span className="text-gray-600">Missing Category</span>
                  <span className="text-amber-600 font-medium">{summary.issues.missing_category}</span>
                </div>
              )}
              {summary.issues.ambiguous_language > 0 && (
                <div className="flex items-center justify-between text-xs">
                  <span className="text-gray-600">Ambiguous Language</span>
                  <span className="text-amber-500 font-medium">{summary.issues.ambiguous_language}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Category breakdown */}
        {summary.by_category && Object.keys(summary.by_category).length > 0 && (
          <div className="pt-3 mt-3 border-t border-gray-100">
            <p className="text-xs font-medium text-gray-500 mb-2">By Category</p>
            <div className="flex flex-wrap gap-2">
              {Object.entries(summary.by_category).map(([cat, count]) => (
                <span key={cat} className="text-xs px-2 py-0.5 bg-gray-100 rounded">
                  {cat}: {count}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </WidgetErrorBoundary>
  );
};

// ==========================================
// MAIN COMPONENT
// ==========================================

const EnhancedScopeWorkspacePage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();

  // TENANT ISOLATION: Use impersonated tenant if sysadmin is impersonating
  const tenantId = localStorage.getItem('impersonated_tenant_id') || user?.tenant?.id || user?.tenant_id;
  const userId = user?.id;

  // State
  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [baselines, setBaselines] = useState([]);
  const [selectedBaseline, setSelectedBaseline] = useState(null);
  
  // View state
  const [activeTab, setActiveTab] = useState('requirements'); // requirements, wbs, templates
  const [showTemplateLibrary, setShowTemplateLibrary] = useState(false);
  
  // AI Assistant state
  const [showAIAssistant, setShowAIAssistant] = useState(false);
  const [selectedRequirementForAI, setSelectedRequirementForAI] = useState(null);
  
  // Spec Upload state
  const [showSpecUpload, setShowSpecUpload] = useState(false);
  
  // Co-Pilot state
  const [showCoPilot, setShowCoPilot] = useState(false);
  const [selectedRequirementForCoPilot, setSelectedRequirementForCoPilot] = useState(null);
  
  // Requirements count state for conditional UI
  const [requirementsCount, setRequirementsCount] = useState(0);
  const [wbsNodesCount, setWbsNodesCount] = useState(0);
  
  // Refresh key to force RequirementsHierarchy to refetch
  const [requirementsRefreshKey, setRequirementsRefreshKey] = useState(0);
  
  // Show decision panel for new users - initialize to null to avoid flash
  const [showDecisionPanel, setShowDecisionPanel] = useState(null);
  
  // Create Project Modal state
  const [showCreateProjectModal, setShowCreateProjectModal] = useState(false);
  
  // WBS Preview Modal state
  const [showWBSPreview, setShowWBSPreview] = useState(false);
  const [wbsGenerating, setWbsGenerating] = useState(false);
  
  // Revert Baseline Modal state  
  const [showRevertModal, setShowRevertModal] = useState(false);
  
  // Store requirements data for Scope Summary
  const [requirementsData, setRequirementsData] = useState([]);
  
  // Derived: Only show decision panel once we know the state and requirements count is 0
  const shouldShowDecisionPanel = showDecisionPanel === true && !loading;
  
  // Handle requirement selection for Co-Pilot
  const handleRequirementSelect = useCallback((requirement) => {
    setSelectedRequirementForCoPilot(requirement);
    if (!showCoPilot) {
      setShowCoPilot(true);
    }
  }, [showCoPilot]);
  
  // Handle applying Co-Pilot suggestions
  const handleApplyCoPilotSuggestion = useCallback(async (suggestion) => {
    if (!suggestion || !selectedRequirementForCoPilot) return;
    
    try {
      // For now, just notify user - actual apply logic depends on suggestion type
      toast.success('Suggestion noted! Edit the requirement to apply changes.');
    } catch (err) {
      toast.error('Failed to apply suggestion');
    }
  }, [selectedRequirementForCoPilot]);
  
  // Handle spec upload completion
  const handleSpecUploadComplete = useCallback((result) => {
    toast.success(`Imported ${result.imported_count} requirements`);
    // Trigger refresh of requirements list
    setActiveTab('requirements');
    // Update requirements count
    setRequirementsCount(prev => prev + (result.imported_count || 0));
    // Hide decision panel after first import
    setShowDecisionPanel(false);
    // Force RequirementsHierarchy to refetch data by changing its key
    setRequirementsRefreshKey(prev => prev + 1);
  }, []);

  // Handle opening AI Assistant for a requirement
  const handleOpenAIAssistant = useCallback((requirement) => {
    setSelectedRequirementForAI(requirement);
    setShowAIAssistant(true);
  }, []);

  // Handle applying AI suggestion to requirement
  const handleApplyAISuggestion = useCallback(async (suggestion) => {
    if (!selectedRequirementForAI) return;
    
    try {
      // Apply the suggestion based on type
      const updates = {};
      
      if (suggestion.type === 'ac' && suggestion.data) {
        const currentAC = selectedRequirementForAI.acceptance_criteria || [];
        updates.acceptance_criteria = [...currentAC, {
          id: `ac_${Date.now()}`,
          ...suggestion.data,
          is_met: false,
        }];
      } else if (suggestion.type === 'risk' && suggestion.data) {
        const currentRisks = selectedRequirementForAI.risks || [];
        updates.risks = [...currentRisks, {
          id: `risk_${Date.now()}`,
          ...suggestion.data,
        }];
      } else if (suggestion.type === 'constraint' && suggestion.data) {
        const currentConstraints = selectedRequirementForAI.constraints || [];
        updates.constraints = [...currentConstraints, {
          id: `constraint_${Date.now()}`,
          ...suggestion.data,
        }];
      } else if (suggestion.type === 'dependency' && suggestion.data) {
        const currentDeps = selectedRequirementForAI.dependencies || [];
        updates.dependencies = [...currentDeps, {
          id: `dep_${Date.now()}`,
          ...suggestion.data,
        }];
      }
      
      if (Object.keys(updates).length > 0) {
        await api.updateRequirement(selectedRequirementForAI.id, tenantId, userId, updates);
        toast.success('Suggestion applied to requirement');
      }
    } catch (err) {
      console.error('Failed to apply suggestion:', err);
      toast.error('Failed to apply suggestion');
      throw err;
    }
  }, [selectedRequirementForAI, tenantId, userId]);

  // Load projects
  useEffect(() => {
    if (tenantId) {
      loadProjects();
    }
  }, [tenantId]);

  // Load baselines when project changes
  useEffect(() => {
    if (selectedProject) {
      loadBaselines();
    }
  }, [selectedProject]);

  const loadProjects = async () => {
    try {
      const res = await api.getScopingProjects(tenantId);
      const projectList = res.data.projects || [];
      setProjects(projectList);

      const projectIdParam = searchParams.get('project');
      if (projectIdParam) {
        const project = projectList.find(p => p.id === projectIdParam);
        if (project) setSelectedProject(project);
      } else if (projectList.length > 0) {
        setSelectedProject(projectList[0]);
      }
    } catch (err) {
      console.error('Failed to load projects:', err);
      toast.error('Failed to load projects');
    } finally {
      setLoading(false);
    }
  };

  const loadBaselines = async () => {
    if (!selectedProject) return;
    try {
      const res = await api.listBaselines(tenantId, selectedProject.id);
      const baselineList = res.data.baselines || [];
      setBaselines(baselineList);
      
      // Select the latest draft or create indication
      const draft = baselineList.find(b => b.status === 'draft');
      const selectedBl = draft || baselineList[0] || null;
      setSelectedBaseline(selectedBl);
      
      // Track counts from the selected baseline
      if (selectedBl) {
        setRequirementsCount(selectedBl.requirement_count || 0);
        setWbsNodesCount(selectedBl.wbs_node_count || 0);
        // Show decision panel for empty projects, hide for projects with requirements
        setShowDecisionPanel((selectedBl.requirement_count || 0) === 0);
      } else {
        // No baseline - show decision panel for new projects
        setRequirementsCount(0);
        setWbsNodesCount(0);
        setShowDecisionPanel(true);
      }
    } catch (err) {
      console.error('Failed to load baselines:', err);
    }
  };

  // Create new project - open modal
  const handleCreateProject = () => {
    setShowCreateProjectModal(true);
  };
  
  // Handle project created from modal
  const handleProjectCreated = async (project) => {
    setShowCreateProjectModal(false);
    toast.success('Project created');
    await loadProjects();
    // API returns project directly
    setSelectedProject(project);
  };

  // Create baseline
  const handleCreateBaseline = async () => {
    if (!selectedProject) return;
    try {
      const res = await api.createBaseline(tenantId, userId, {
        project_id: selectedProject.id,
      });
      toast.success('Baseline created');
      loadBaselines();
      setSelectedBaseline(res.data.baseline);
    } catch (err) {
      console.error('Failed to create baseline:', err);
      toast.error('Failed to create baseline');
    }
  };

  // Show WBS Preview Modal
  const handleShowWBSPreview = () => {
    if (!selectedProject) return;
    setShowWBSPreview(true);
  };

  // Generate WBS after confirmation
  const handleConfirmGenerateWBS = async () => {
    if (!selectedProject) return;
    setWbsGenerating(true);
    
    try {
      await api.generateWBS(tenantId, userId, {
        project_id: selectedProject.id,
        baseline_id: selectedBaseline?.id,
        include_phases: true,
      });
      toast.success('WBS generated from requirements');
      setShowWBSPreview(false);
      setActiveTab('wbs');
      loadBaselines();
    } catch (err) {
      console.error('Failed to generate WBS:', err);
      toast.error(err.response?.data?.detail || 'Failed to generate WBS');
    } finally {
      setWbsGenerating(false);
    }
  };

  // Reset WBS (delete all WBS nodes)
  const handleResetWBS = async () => {
    if (!selectedProject || !selectedBaseline) return;
    
    try {
      await api.delete(`/api/wbs/reset/${selectedBaseline.id}?tenant_id=${tenantId}`);
      toast.success('WBS has been reset');
      setShowWBSPreview(false);
      loadBaselines();
    } catch (err) {
      console.error('Failed to reset WBS:', err);
      toast.error(err.response?.data?.detail || 'Failed to reset WBS');
    }
  };

  // Revert to previous baseline
  const handleRevertBaseline = async (baseline) => {
    if (!baseline) return;
    
    try {
      // TODO: Implement baseline revert logic on backend
      toast.success(`Reverted to ${baseline.name}`);
      setShowRevertModal(false);
      loadBaselines();
    } catch (err) {
      toast.error('Failed to revert baseline');
    }
  };

  // Navigate to baselines page
  const handleViewBaselines = () => {
    navigate(`/scope/baselines?project=${selectedProject?.id}`);
  };

  const isReadOnly = selectedBaseline?.status === 'approved';

  if (loading) {
    return (
      <div className="p-6">
        <div className="h-8 bg-gray-200 rounded w-64 animate-pulse mb-6" />
        <SkeletonKPIGrid count={4} className="mb-6" />
        <div className="grid grid-cols-3 gap-6">
          <div className="col-span-2">
            <SkeletonCard className="h-96" />
          </div>
          <SkeletonCard className="h-96" />
        </div>
      </div>
    );
  }

  return (
    <PageErrorBoundary pageName="Scope Workspace">
      <div className="p-6 max-w-7xl mx-auto" data-testid="scope-workspace-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Scope & Requirements</h1>
            <p className="text-gray-500 text-sm">
              Capture requirements, build hierarchy, and generate WBS
            </p>
          </div>
          <div className="flex items-center gap-3">
            {/* Project selector */}
            <select
              value={selectedProject?.id || ''}
              onChange={(e) => {
                const project = projects.find(p => p.id === e.target.value);
                setSelectedProject(project);
              }}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[var(--clr-brand-primary)] focus:outline-none"
              data-testid="project-selector"
            >
              <option value="">Select Project...</option>
              {projects.map(p => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
            
            <button
              onClick={handleCreateProject}
              className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50"
              data-testid="new-project-btn"
            >
              <Plus className="w-4 h-4" />
              New Project
            </button>

            <button
              onClick={handleViewBaselines}
              className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50"
              data-testid="view-baselines-btn"
            >
              <GitCompare className="w-4 h-4" />
              Baselines
            </button>
          </div>
        </div>

        {!selectedProject ? (
          <EmptyState
            icon={FolderKanban}
            title="No project selected"
            description="Select or create a project to start capturing requirements."
            action={{
              label: "Create Project",
              onClick: handleCreateProject,
            }}
          />
        ) : (
          <>
            {/* Step Progress Bar */}
            <StepProgressBar
              currentStep={activeTab}
              hasRequirements={requirementsCount > 0}
              hasWBS={wbsNodesCount > 0}
              hasBaseline={!!selectedBaseline}
              hasProject={false}
            />

            {/* Decision Panel - Show for new/empty projects */}
            {shouldShowDecisionPanel && requirementsCount === 0 && (
              <StartDecisionPanel
                onUploadSpec={() => setShowSpecUpload(true)}
                onStartWizard={() => navigate('/scope/wizard')}
                onOpenCoPilot={() => setShowCoPilot(true)}
                isReadOnly={isReadOnly}
              />
            )}

            {/* Baseline Info Bar */}
            <div className="mb-6 p-4 bg-gray-50 rounded-lg border flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div>
                  <span className="text-sm text-gray-500">Current Baseline:</span>
                  {selectedBaseline ? (
                    <span className={`ml-2 px-2 py-0.5 rounded text-sm font-medium ${
                      selectedBaseline.status === 'approved' 
                        ? 'bg-green-100 text-green-700' 
                        : selectedBaseline.status === 'submitted'
                          ? 'bg-blue-100 text-blue-700'
                          : 'bg-amber-100 text-amber-700'
                    }`}>
                      V{selectedBaseline.version} ({selectedBaseline.status})
                    </span>
                  ) : (
                    <span className="ml-2 text-sm text-gray-600">None</span>
                  )}
                </div>

                {isReadOnly && (
                  <div className="flex items-center gap-1 text-xs text-amber-600">
                    <AlertTriangle className="w-3.5 h-3.5" />
                    Read-only (baseline is approved)
                  </div>
                )}
              </div>

              <div className="flex items-center gap-2">
                {/* Co-Pilot Toggle */}
                <button
                  onClick={() => setShowCoPilot(!showCoPilot)}
                  className={`flex items-center gap-2 px-3 py-1.5 text-sm rounded-lg transition-all ${
                    showCoPilot 
                      ? 'bg-purple-600 text-white' 
                      : 'border border-purple-300 text-purple-700 hover:bg-purple-50'
                  }`}
                  data-testid="copilot-toggle-btn"
                >
                  <MessageSquare className="w-4 h-4" />
                  Co-Pilot
                </button>
                {/* Agentic PM Link */}
                <Link
                  to="/ai/agentic-pm"
                  state={{ from: '/scope/workspace' }}
                  className="flex items-center gap-2 px-3 py-1.5 border border-indigo-300 text-indigo-700 hover:bg-indigo-50 text-sm rounded-lg transition-all"
                  data-testid="agentic-pm-link-btn"
                >
                  <Brain className="w-4 h-4" />
                  Agentic PM
                </Link>
                {/* Wizard Button */}
                <button
                  onClick={() => navigate('/scope/wizard')}
                  className="flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-purple-600 to-indigo-600 text-white text-sm rounded-lg hover:opacity-90 transition-all shadow-sm"
                  data-testid="wizard-btn"
                >
                  <Sparkles className="w-4 h-4" />
                  Wizard
                </button>
                {/* Upload Requirements Button */}
                {selectedProject && !isReadOnly && (
                  <button
                    onClick={() => setShowSpecUpload(true)}
                    className="flex items-center gap-2 px-3 py-1.5 border border-purple-300 text-purple-700 text-sm rounded-lg hover:bg-purple-50 transition-colors"
                    data-testid="upload-requirements-btn"
                  >
                    <Upload className="w-4 h-4" />
                    Upload Spec
                  </button>
                )}
                {/* Export Documents - Disabled until baseline exists */}
                {selectedProject && (
                  <div className={!selectedBaseline ? 'opacity-50 cursor-not-allowed' : ''} title={!selectedBaseline ? 'Create a baseline first' : ''}>
                    <ExportDocumentsDropdown
                      projectId={selectedProject.id}
                      tenantId={tenantId}
                      baselineId={selectedBaseline?.id}
                      disabled={!selectedBaseline}
                    />
                  </div>
                )}
                {!selectedBaseline && (
                  <button
                    onClick={handleCreateBaseline}
                    className="flex items-center gap-2 px-3 py-1.5 bg-[var(--clr-brand-primary)] text-white text-sm rounded-lg hover:opacity-90"
                    data-testid="create-baseline-btn"
                  >
                    <Plus className="w-4 h-4" />
                    Create Baseline
                  </button>
                )}
                {/* Generate WBS - Disabled until requirements exist */}
                {selectedBaseline && selectedBaseline.status === 'draft' && (
                  <button
                    onClick={handleShowWBSPreview}
                    disabled={requirementsCount === 0}
                    className={`flex items-center gap-2 px-3 py-1.5 border border-gray-300 text-sm rounded-lg transition-colors ${
                      requirementsCount === 0 
                        ? 'opacity-50 cursor-not-allowed bg-gray-100' 
                        : 'hover:bg-white'
                    }`}
                    data-testid="generate-wbs-btn"
                    title={requirementsCount === 0 ? 'Add requirements first' : 'Generate WBS from requirements'}
                  >
                    <FolderKanban className="w-4 h-4" />
                    Generate WBS
                  </button>
                )}
                
                {/* Continue in Wizard */}
                <button
                  onClick={() => navigate(`/scope/wizard?project=${selectedProject?.id}`)}
                  className="flex items-center gap-2 px-3 py-1.5 border border-purple-300 text-purple-700 text-sm rounded-lg hover:bg-purple-50 transition-colors"
                  data-testid="continue-wizard-btn"
                  title="Continue scoping with AI-guided wizard"
                >
                  <Rocket className="w-4 h-4" />
                  Continue in Wizard
                </button>
              </div>
            </div>

            {/* Tab Navigation */}
            <div className="mb-6 border-b">
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setActiveTab('requirements')}
                  className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === 'requirements'
                      ? 'border-[var(--clr-brand-primary)] text-[var(--clr-brand-primary)]'
                      : 'border-transparent text-gray-600 hover:text-gray-900'
                  }`}
                  data-testid="tab-requirements"
                >
                  <Layers className="w-4 h-4 inline mr-2" />
                  Requirements
                </button>
                <button
                  onClick={() => setActiveTab('summary')}
                  className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === 'summary'
                      ? 'border-[var(--clr-brand-primary)] text-[var(--clr-brand-primary)]'
                      : 'border-transparent text-gray-600 hover:text-gray-900'
                  }`}
                  data-testid="tab-summary"
                >
                  <PieChart className="w-4 h-4 inline mr-2" />
                  Scope Summary
                </button>
                <button
                  onClick={() => setActiveTab('wbs')}
                  className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === 'wbs'
                      ? 'border-[var(--clr-brand-primary)] text-[var(--clr-brand-primary)]'
                      : 'border-transparent text-gray-600 hover:text-gray-900'
                  }`}
                  data-testid="tab-wbs"
                >
                  <FolderKanban className="w-4 h-4 inline mr-2" />
                  WBS
                </button>
                <button
                  onClick={() => navigate('/admin/templates')}
                  className="px-4 py-2 text-sm font-medium border-b-2 border-transparent text-gray-600 hover:text-gray-900"
                  data-testid="tab-templates"
                >
                  <BookTemplate className="w-4 h-4 inline mr-2" />
                  Templates
                </button>
              </div>
            </div>

            {/* Main Content Grid */}
            <div className="grid grid-cols-4 gap-6">
              {/* Main Content Area */}
              <div className="col-span-3">
                {activeTab === 'requirements' && (
                  <WidgetErrorBoundary widgetName="Requirements">
                    <div className="bg-white rounded-lg border p-4 min-h-[500px]">
                      <RequirementsHierarchy
                        key={`req-hierarchy-${requirementsRefreshKey}`}
                        projectId={selectedProject.id}
                        tenantId={tenantId}
                        userId={userId}
                        baselineId={selectedBaseline?.id}
                        isReadOnly={isReadOnly}
                        showHeader={true}
                        showFilters={true}
                        onOpenAIAssist={handleOpenAIAssistant}
                        onRequirementSelect={handleRequirementSelect}
                        onDataLoaded={(items) => {
                          const count = items?.length || 0;
                          setRequirementsCount(count);
                          setRequirementsData(items || []);
                          // Show decision panel for empty projects, hide for projects with requirements
                          setShowDecisionPanel(count === 0);
                        }}
                      />
                    </div>
                  </WidgetErrorBoundary>
                )}

                {activeTab === 'summary' && (
                  <WidgetErrorBoundary widgetName="Scope Summary">
                    <div className="bg-white rounded-lg border p-4 min-h-[500px]">
                      <ScopeSummary
                        projectId={selectedProject.id}
                        tenantId={tenantId}
                        requirements={requirementsData}
                        onNavigateToRequirement={(req) => {
                          setSelectedRequirementForCoPilot(req);
                          setActiveTab('requirements');
                          setShowCoPilot(true);
                        }}
                        onDataChanged={() => {
                          // Trigger refresh of requirements
                          setRequirementsRefreshKey(prev => prev + 1);
                        }}
                      />
                    </div>
                  </WidgetErrorBoundary>
                )}

                {activeTab === 'wbs' && (
                  <WidgetErrorBoundary widgetName="WBS">
                    <div className="bg-white rounded-lg border p-4 min-h-[500px]">
                      {selectedBaseline ? (
                        <WBSTree
                          projectId={selectedProject.id}
                          tenantId={tenantId}
                          baselineId={selectedBaseline.id}
                          showHeader={true}
                          onDataLoaded={(nodes) => setWbsNodesCount(nodes?.length || 0)}
                        />
                      ) : (
                        <EmptyState
                          icon={FolderKanban}
                          title="No baseline selected"
                          description="Create a baseline and add requirements to generate WBS."
                          action={{
                            label: "Create Baseline",
                            onClick: handleCreateBaseline,
                          }}
                        />
                      )}
                    </div>
                  </WidgetErrorBoundary>
                )}
              </div>

              {/* Sidebar */}
              <div className="col-span-1 space-y-4">
                {/* Quality Summary */}
                {selectedProject && (
                  <QualitySummaryCard
                    projectId={selectedProject.id}
                    tenantId={tenantId}
                  />
                )}

                {/* Quick Actions */}
                <WidgetErrorBoundary widgetName="Quick Actions">
                  <div className="bg-white rounded-lg border p-4">
                    <h4 className="text-sm font-semibold text-gray-700 mb-3">Quick Actions</h4>
                    <div className="space-y-2">
                      <button
                        onClick={() => navigate('/admin/templates')}
                        className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-gray-50 rounded-lg transition-colors"
                      >
                        <BookTemplate className="w-4 h-4 text-gray-500" />
                        Manage Templates
                      </button>
                      <button
                        onClick={handleViewBaselines}
                        className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-gray-50 rounded-lg transition-colors"
                      >
                        <GitCompare className="w-4 h-4 text-gray-500" />
                        Compare Baselines
                      </button>
                      {selectedBaseline && selectedBaseline.status === 'draft' && (
                        <button
                          onClick={handleShowWBSPreview}
                          className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-gray-50 rounded-lg transition-colors"
                        >
                          <FolderKanban className="w-4 h-4 text-gray-500" />
                          Generate WBS
                        </button>
                      )}
                      {selectedBaseline && wbsNodesCount > 0 && (
                        <button
                          onClick={() => setShowRevertModal(true)}
                          className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-gray-50 rounded-lg transition-colors text-amber-600"
                        >
                          <Undo2 className="w-4 h-4" />
                          Revert to Baseline
                        </button>
                      )}
                    </div>
                  </div>
                </WidgetErrorBoundary>

                {/* Info Panel */}
                <div className="bg-blue-50 rounded-lg border border-blue-200 p-4">
                  <div className="flex items-start gap-2">
                    <Info className="w-5 h-5 text-blue-500 mt-0.5" />
                    <div>
                      <h4 className="text-sm font-semibold text-blue-700 mb-1">PMO-Ready Structure</h4>
                      <p className="text-xs text-blue-600">
                        Use the Theme → Epic → Requirement hierarchy to organize your scope. 
                        Add acceptance criteria, risks, and constraints to each requirement for 
                        complete traceability.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        {/* AI Requirement Assistant Modal */}
        <AIRequirementAssistant
          isOpen={showAIAssistant}
          onClose={() => {
            setShowAIAssistant(false);
            setSelectedRequirementForAI(null);
          }}
          requirement={selectedRequirementForAI}
          tenantId={tenantId}
          onApplySuggestion={handleApplyAISuggestion}
        />

        {/* Spec Upload Modal */}
        <SpecUploadModal
          isOpen={showSpecUpload}
          onClose={() => setShowSpecUpload(false)}
          tenantId={tenantId}
          projectId={selectedProject?.id}
          userId={userId}
          onImportComplete={handleSpecUploadComplete}
        />

        {/* Scoping Co-Pilot */}
        <ScopingCoPilot
          isOpen={showCoPilot}
          onClose={() => setShowCoPilot(false)}
          tenantId={tenantId}
          projectId={selectedProject?.id}
          selectedRequirement={selectedRequirementForCoPilot}
          onApplySuggestion={handleApplyCoPilotSuggestion}
        />

        {/* Create Project Modal */}
        <CreateProjectModal
          isOpen={showCreateProjectModal}
          onClose={() => setShowCreateProjectModal(false)}
          onProjectCreated={handleProjectCreated}
        />

        {/* WBS Preview Modal */}
        <WBSPreviewModal
          isOpen={showWBSPreview}
          onClose={() => setShowWBSPreview(false)}
          onConfirm={handleConfirmGenerateWBS}
          onReset={handleResetWBS}
          requirementsCount={requirementsCount}
          existingWBSCount={wbsNodesCount}
          baselineName={selectedBaseline?.name}
          loading={wbsGenerating}
        />

        {/* Revert Baseline Modal */}
        <RevertBaselineModal
          isOpen={showRevertModal}
          onClose={() => setShowRevertModal(false)}
          onRevert={handleRevertBaseline}
          baselines={baselines}
          currentBaselineId={selectedBaseline?.id}
        />
      </div>
    </PageErrorBoundary>
  );
};

export default EnhancedScopeWorkspacePage;
